import { TerminalApi } from '@/apis/terminal.aspi';
import PaginationComponent from '@/components/common/pagination/component/pagination';
import { TerminalType } from '@/constants/app.enums';
import { ROUTE_PATHS } from '@/constants/router.constants';
import { Terminal } from '@/models/terminal';
import {
  CheckCircleOutlined,
  LinkOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import { Badge, Button, Card, Col, Flex, message, Modal, Row, Space, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Key, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './index.scss';
import ModalDeviceLinking from './modal/modalListTerminalLinking';
import showNotification from '@/components/common/notification';
import showConfirmModal from '@/components/common/modal-confirm';
import { DefaultDeviceRequest } from '@/models/device';

interface TerminalDetailProps {
  terminal?: Terminal;
}

function DeviceLinking(props: TerminalDetailProps) {
  const { t } = useTranslation();
  const [isLoadingTerminal, setLoadingTerminal] = useState(false);
  const [apiManagement, setApiManagement] = useState<Terminal[] | any[]>();
  const [pageSize, setPageSize] = useState(5);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [isModalDeviceLinkingOpen, setIsModalDeviceLinkingOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  useEffect(() => {
    fetchData();
    setSearchText("");
  }, []);

  const fetchData = async () => {
    await handleGetTerminalList(pageSize, pageNumber, undefined);
  };

  const handleShowDeviceLinking = () => {
    setIsModalDeviceLinkingOpen(true);
  };

  const handleGetTerminalList = async (
    pageSizeData?: number,
    pageNumberData?: number,
    searchString?: string,
  ) => {
    pageSizeData = pageSizeData || pageSize;
    pageNumberData = pageNumberData || pageNumber;
    searchString = searchString || '';
    setLoadingTerminal(true);
    try {
      const { data } = props.terminal?.terminalType === TerminalType.Kiosk ?
        await TerminalApi.getPaymentsByKiosk(
          props.terminal?.id ?? "",
          pageNumberData,
          pageSizeData,
          searchString
        ) :
        await TerminalApi.getKiosksByPayment(
          props.terminal?.id ?? "",
          pageNumberData,
          pageSizeData,
          searchString
        );

      setApiManagement(data.records);
      setTotalRecords(data.totalRecords);
    } catch (error) { }
    finally {
      setLoadingTerminal(false);
      setSearchText(searchString);
    }
  };

  const columns: ColumnsType<Terminal> = [
    {
      title: t('device.title.terminalName'),
      key: '1',
      render: (text: string, record: any) => <>{record.lszTerminalName}</>,
    },
    {
      title: t('device.title.terminalSerialNumber'),
      key: '2',
      render: (text: string, record: any) => <>{record.terminalSerialNumber}</>,
    },
    {
      title: t('device.title.brand'),
      key: '3',
      render: (text: string, record: any) => (
        <>
          <Row> {record.brand}</Row>
        </>
      ),
    },
    {
      title: t('device.title.model'),
      key: '4',
      render: (text: string, record: any) => (
        <>
          <Row> {record.model}</Row>
        </>
      ),
    },
    {
      title: t('api_management.title.status'),
      key: '5',
      className: 'col-status',
      dataIndex: 'isDeactivate',
      render: (text: string, record: any) => {
        return (
          <>
            {record.isDeactivate && (
              <Badge
                status="error"
                text="Deactivate"
              />
            )}
            {!record.isDeactivate && (
              <Badge
                status="success"
                text="Activate"
              />
            )}

            {record.isDefault === true && <Tag style={{ marginLeft: "20px" }} icon={<CheckCircleOutlined />} color="success"> Default </Tag>}
          </>
        );
      },
    },
  ];

  const handleSetDefault  = () => {
    const handleConfirm = async () => {
      try {
        const model: DefaultDeviceRequest = {
          sourceDeviceId: props.terminal?.id,
          selectedDeviceId: selectedRowKeys[0].toString()
        };
        const { data } = await TerminalApi.setDefaultDevice(model);
        if (data) {
          message.success('Default has been successfully changed.');
          fetchData();
        }
      } catch (error) { }
    };

    const handleCancel = () => {
      message.info('Action canceled.');
    };

    showConfirmModal({
      type: "warning",
      title: `Are you sure you want to change the default device linking?`,
      content: t("modal.confirm_text"),
      onOk: handleConfirm,
      onCancel: handleCancel,
    });
  };

  const handleCloseDeviceLinkingModal = (isUpdated?: boolean) => {
    setIsModalDeviceLinkingOpen(false);
    if(isUpdated){
      fetchData();
    }
  };

  return (
    <Row gutter={24}>
      <Col span={24}>
        <Card
          style={{ marginTop: 16 }}
          title="Device"
          extra={
            <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                style={{background: "#0785d9"}}
                onClick={handleShowDeviceLinking}
                icon={<LinkOutlined />}
                type="primary">
                Add Linking
              </Button>
              {selectedRowKeys.length > 0 && (
                <Button type="dashed" onClick={handleSetDefault} icon={<CheckCircleOutlined />}>Set Default</Button>
              )}
            </Space>

          }
        >
          <Row>
            <Col span={24}>
              <Table
                rowSelection={{
                  type: 'radio',
                  selectedRowKeys,
                  onChange: (selectedRowKeys: Key[]) => {
                    setSelectedRowKeys(selectedRowKeys);
                  },
                }}
                onRow={(record) => ({
                  onClick: () => {
                    console.log(selectedRowKeys[0])

                    if (selectedRowKeys[0] && selectedRowKeys[0] === record.id) {
                      console.log(record);
                      console.log(selectedRowKeys)
                      setSelectedRowKeys([]);
                    } else {
                      setSelectedRowKeys([record.id]);
                    }
                  },
                })}
                loading={isLoadingTerminal}
                rowKey={(x) => x.id}
                className="tb-list-terminal"
                columns={columns}
                dataSource={apiManagement}
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: pageSize,
                  current: pageNumber,
                  style: { display: 'none' },
                }}
              />
            </Col>
            <PaginationComponent
              totalRecords={totalRecords}
              pageSize={pageSize}
              setPageSize={setPageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              handleGetFilter={handleGetTerminalList}
            />
          </Row>
        </Card>
      </Col>

      <ModalDeviceLinking
        visible={isModalDeviceLinkingOpen}
        onClose={handleCloseDeviceLinkingModal}
        terminal={props.terminal}
      />
    </Row>

  );
}
export default DeviceLinking;
